<template>
  <v-main>
    <v-container fluid>
      <!-- Header -->
      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-title>
              <AppPageHeader
                icon="mdi-cart"
                :title="isDeletePage() != true ? 'Orders' : 'Deleted Orders'"
                subtitle="Customer and counter sales, in one place"
                style="width: 100%"
              >
                <v-btn
                  v-if="this.filter.store_id != ''"
                  fab
                  x-small
                  color="primary"
                  @click="$router.push('/orders')"
                >
                  <v-icon dark>mdi-arrow-left</v-icon>
                </v-btn>
              </AppPageHeader>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Summary Cards -->
      <v-row align="stretch">
        <v-col cols="12" sm="6" md="3">
          <v-card elevation="0" style="border:1px solid #e0e0e0" class="fill-height stat-card">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text stat-label">Total Sales</div>
                <div class="text-h5 font-weight-bold stat-value mono-figure">
                  {{ formatAmount(stats.total_sales) }}
                </div>
                <div class="text-caption grey--text stat-sub">
                  Avg {{ formatAmount(stats.avg_order_value) }} per order
                </div>
              </div>
              <v-avatar size="44" color="grey lighten-3">
                <v-icon color="var(--color-ink)" size="22">mdi-currency-inr</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card elevation="0" style="border:1px solid #e0e0e0" class="fill-height stat-card">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text stat-label">App Orders</div>
                <div class="text-h5 font-weight-bold stat-value mono-figure info--text">
                  {{ formatAmount(stats.channels.app.sales) }}
                </div>
                <div class="text-caption grey--text stat-sub">
                  {{ stats.channels.app.orders || 0 }} orders · customer self-service
                </div>
              </div>
              <v-avatar size="44" color="blue lighten-4">
                <v-icon color="info" size="22">mdi-cellphone</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card elevation="0" style="border:1px solid #e0e0e0" class="fill-height stat-card">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text stat-label">Counter Sales</div>
                <div class="text-h5 font-weight-bold stat-value mono-figure success--text">
                  {{ formatAmount(stats.channels.counter.sales) }}
                </div>
                <div class="text-caption grey--text stat-sub">
                  {{ stats.channels.counter.orders || 0 }} orders · handed over in person
                </div>
              </div>
              <v-avatar size="44" color="success lighten-4">
                <v-icon color="success" size="22">mdi-storefront-outline</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card elevation="0" style="border:1px solid #e0e0e0" class="fill-height stat-card">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text stat-label">Needs Action</div>
                <div
                  class="text-h5 font-weight-bold stat-value mono-figure"
                  :class="needsAction > 0 ? 'error--text' : ''"
                >{{ needsAction }}</div>
                <div class="text-caption grey--text stat-sub">
                  {{ stats.needs_action.stale_count || 0 }} stuck 2+ days ·
                  {{ stats.needs_action.unassigned_count || 0 }} unassigned
                </div>
              </div>
              <v-avatar size="44" :color="needsAction > 0 ? 'red lighten-4' : 'grey lighten-3'">
                <v-icon :color="needsAction > 0 ? 'error' : 'var(--color-ink)'" size="22">mdi-alert-circle-outline</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filters -->
      <v-row>
        <v-col md="12">
          <v-card elevation="0" style="border:1px solid #e0e0e0">
            <v-card-text>
              <div class="text-overline grey--text mb-1">
                <v-icon small class="mr-1">mdi-filter-variant</v-icon>Filters
              </div>
              <v-row>
                <v-col md="3">
                  <v-text-field
                    outlined
                    dense
                    v-model="filter.search_keyword"
                    label="Search ... "
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  >
                  </v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-select
                    outlined
                    dense
                    v-model="filter.order_status"
                    :items="statusItems"
                    item-text="text"
                    item-value="value"
                    label="Order Status"
                    hide-details
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>
                <v-col sm="3">
                  <v-select
                    outlined
                    dense
                    v-model="filter.payment_status"
                    :items="paymentStatusItems"
                    item-text="text"
                    item-value="value"
                    label="Payment Status"
                    hide-details
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>
                <v-col sm="3" v-if="this.filter.store_id != ''">
                  <v-autocomplete
                    outlined
                    dense
                    ref="product"
                    v-model="filter.product_id"
                    :items="productItems"
                    hide-details
                    label="Product"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-toggle-switch"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <!-- Month Year -->
                <v-col sm="3">
                  <v-menu
                    v-model="month_menu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        outlined
                        dense
                        v-model="filter.month_year"
                        label="Year - Month"
                        append-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        clearable
                        @click:clear="
                          () => {
                            filter.month_year = null;
                            getData();
                          }
                        "
                        hide-details
                        :error-messages="errors.month"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.month_year"
                      type="month"
                      @input="
                        getData();
                        month_menu = false;
                      "
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <!-- /Month Year -->
                <!-- Start date -->
                <v-col sm="3">
                  <v-menu
                    v-model="start_menu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        outlined
                        dense
                        v-model="filter.start_date"
                        label="Start Date"
                        append-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        clearable
                        @click:clear="
                          () => {
                            filter.start_date = null;
                            getData();
                          }
                        "
                        hide-details
                        :error-messages="errors.start_date"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.start_date"
                      @input="
                        getData();
                        start_menu = false;
                      "
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <!-- /Start date -->
                <!-- End date -->
                <v-col sm="3">
                  <v-menu
                    v-model="end_menu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        outlined
                        dense
                        v-model="filter.end_date"
                        label="End Date"
                        append-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        clearable
                        @click:clear="
                          () => {
                            filter.end_date = null;
                            getData();
                          }
                        "
                        hide-details
                        :error-messages="errors.end_date"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.end_date"
                      @input="
                        getData();
                        end_menu = false;
                      "
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <!-- /End date -->
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Channel tabs -->
      <v-card elevation="0" rounded="xxl" class="mb-4 app-card">
        <v-tabs
          v-model="channelTab"
          background-color="transparent"
          color="primary"
          show-arrows
        >
          <v-tab>
            All
            <v-chip x-small class="ml-2">{{ stats.total_orders || 0 }}</v-chip>
          </v-tab>
          <v-tab>
            App Orders
            <v-chip x-small class="ml-2">{{ stats.channels.app.orders || 0 }}</v-chip>
          </v-tab>
          <v-tab>
            Counter Sales
            <v-chip x-small class="ml-2">{{ stats.channels.counter.orders || 0 }}</v-chip>
          </v-tab>
        </v-tabs>
      </v-card>

      <!-- Fulfilment pipeline — app orders only; counter sales never travel -->
      <v-row v-if="channel !== 'counter'" class="mb-2" align="stretch">
        <v-col
          v-for="stage in stats.pipeline"
          :key="`stage_${stage.status}`"
          cols="6"
          sm="4"
          md="2"
        >
          <v-card
            elevation="2"
            rounded="lg"
            class="fill-height pipeline-card"
            :class="{ 'pipeline-active': String(filter.order_status) === String(stage.status) }"
            @click="filterByStage(stage.status)"
          >
            <v-card-text class="py-2">
              <div class="text-caption grey--text">{{ stage.label }}</div>
              <div class="text-h6 font-weight-bold">{{ stage.count }}</div>
              <div class="text-caption grey--text">
                {{ formatAmount(stage.amount) }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card elevation="2" rounded="lg" class="fill-height">
            <v-card-text class="py-2">
              <div class="text-caption grey--text">Payment Split</div>
              <div class="text-caption">
                Cash {{ formatAmount(stats.payment.cash_amount) }}
              </div>
              <div class="text-caption">
                Online {{ formatAmount(stats.payment.online_amount) }}
              </div>
              <div
                v-if="stats.payment.unpaid_count"
                class="text-caption orange--text font-weight-bold"
              >
                {{ stats.payment.unpaid_count }} unpaid
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table
                v-else
                fixed-header
                height="900"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-start sr_no">Sr No</th>
                      <!-- <th class="text-left">Status</th> -->
                      <th class="text-left">Order Status</th>
                      <th class="text-left">Store Name</th>
                      <th class="text-left">Customer Name</th>
                      <th class="text-left" style="min-width: none">Date</th>
                      <th class="text-left" style="min-width: none">
                        Order No
                      </th>
                      <th class="text-left" style="min-width: none">Address</th>
                      <th class="text-left" style="min-width: none">
                        Total Amount
                      </th>
                      <th class="text-left" style="min-width: none">
                        Tax Amount
                      </th>
                      <th class="text-left" style="min-width: none">
                        Delivery Charge
                      </th>
                      <th class="text-left" style="min-width: none">
                        Final Amount
                      </th>
                      <th class="text-left" style="min-width: none">
                        {{ channel === "counter" ? "Sold By" : "Order Created By" }}
                      </th>
                      <th class="text-left" style="min-width: none">Payment</th>
                      <template v-if="channel !== 'counter'">
                        <th class="text-left" style="min-width: none">
                          Transporter Manager
                        </th>
                        <th class="text-left" style="min-width: none">
                          Delivery Date
                        </th>
                        <th class="text-left" style="min-width: 160px">
                          Update Status
                        </th>
                      </template>
                      <th class="text-right action_th" style="min-width: 250px">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody v-if="orders.length != 0">
                    <tr
                      v-for="(order, i) in orders"
                      :key="`order_${i}`"
                      :class="{
                        'table-row-even': i % 2 === 0,
                        'table-row-odd': i % 2 !== 0,
                        'table-row-last': orders.length - 1 == i,
                      }"
                    >
                      <td>&nbsp;{{ i + 1 }}</td>
                      <!-- <td>
                        <v-tooltip bottom v-if="order.is_active == 1">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="teal"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15"
                                  >mdi-checkbox-marked-circle</v-icon
                                >
                              </v-avatar>
                              Active
                            </v-chip>
                          </template>
                          <span>Order is Active</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="order.is_active == 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="red"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-close-circle</v-icon>
                              </v-avatar>
                              Inactive
                            </v-chip>
                          </template>
                          <span>Order is InActive</span>
                        </v-tooltip>
                      </td> -->
                      <td>
                        <!-- Pending Order -->
                        <v-tooltip bottom v-if="order.order_status == 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="orange"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15"
                                  >mdi-checkbox-marked-circle</v-icon
                                >
                              </v-avatar>
                              Pending
                            </v-chip>
                          </template>
                          <span>Order is Pending</span>
                        </v-tooltip>
                        <!-- Dispatch Order -->
                        <v-tooltip bottom v-if="order.order_status == 1">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="green"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15"
                                  >mdi-checkbox-marked-circle</v-icon
                                >
                              </v-avatar>
                              Confirmed
                            </v-chip>
                          </template>
                          <span>Order is Confirmed</span>
                        </v-tooltip>
                        <!-- Parcel Pakaged -->
                        <v-tooltip bottom v-if="order.order_status == 2">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="teal"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15"
                                  >mdi-checkbox-marked-circle</v-icon
                                >
                              </v-avatar>
                              Parcel Pakaged
                            </v-chip>
                          </template>
                          <span>Order is Parcel Pakaged</span>
                        </v-tooltip>
                        <!-- Out Of Delivery -->
                        <v-tooltip bottom v-if="order.order_status == 3">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="blue"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15"
                                  >mdi-checkbox-marked-circle</v-icon
                                >
                              </v-avatar>
                              Out Of Delivery
                            </v-chip>
                          </template>
                          <span>Order is Out Of Delivery</span>
                        </v-tooltip>
                        <!-- Delivered Order -->
                        <v-tooltip bottom v-if="order.order_status == 4">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="green darken-2"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15"
                                  >mdi-checkbox-marked-circle</v-icon
                                >
                              </v-avatar>
                              Delivered
                            </v-chip>
                          </template>
                          <span>Order is Delivered</span>
                        </v-tooltip>
                        <!-- Reject Order -->
                        <v-tooltip bottom v-if="order.order_status == 5">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="red"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-close-circle</v-icon>
                              </v-avatar>
                              Rejected
                            </v-chip>
                          </template>
                          <span>Order is Rejected</span>
                        </v-tooltip>
                        <!-- Retuen Order -->
                        <v-tooltip bottom v-if="order.order_status == 6">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="red"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-close-circle</v-icon>
                              </v-avatar>
                              Return
                            </v-chip>
                          </template>
                          <span>Order is Return</span>
                        </v-tooltip>
                      </td>
                      <td>
                        {{ order.store ? order.store.name : "" }}
                      </td>
                      <td>
                        <span v-if="order.customer">
                          {{
                            (order.customer.first_name || "N/A") +
                            " " +
                            (order.customer.last_name || "N/A")
                          }}
                          <span
                            v-if="order.customer.position"
                            class="text-caption grey--text"
                          >
                            ({{
                              getPositionAbbr(order.customer.position.name)
                            }})
                          </span>
                        </span>
                      </td>
                      <td>{{ order.created_at | niceDate }}</td>

                      <td>
                        {{ order.order_no || "" }}
                        <v-chip v-if="order.parent_order_id" x-small color="blue" text-color="white" class="ml-1">Replacement</v-chip>
                      </td>
                      <td>
                        {{ order.address || "" }}
                      </td>
                      <td>
                        {{ order.total_amount || "" }}
                      </td>
                      <td>
                        {{ order.tax_amount || "" }}
                      </td>
                      <td>
                        {{ order.delivery_charge || "" }}
                      </td>
                      <td>
                        {{ order.final_amount || "" }}
                      </td>
                      <td>
                        <span v-if="order.created_by">
                          {{
                            (order.created_by.first_name || "N/A") +
                            " " +
                            (order.created_by.last_name || "N/A")
                          }}
                          <span
                            v-if="order.created_by.position"
                            class="text-caption grey--text"
                          >
                            ({{
                              getPositionAbbr(order.created_by.position.name)
                            }})
                          </span>
                        </span>
                      </td>
                      <td>
                        <v-chip
                          x-small
                          :color="order.payment_status == 1 ? 'teal' : 'orange'"
                          text-color="white"
                        >
                          {{ order.payment_status == 1 ? "Paid" : "Unpaid" }}
                        </v-chip>
                        <div class="text-caption grey--text">
                          {{ order.payment_mode == 1 ? "Online" : "Cash" }}
                        </div>
                      </td>
                      <td v-if="channel !== 'counter'">
                        <span v-if="order.transporter_manager">
                          {{
                            (order.transporter_manager.first_name || "N/A") +
                            " " +
                            (order.transporter_manager.last_name || "N/A")
                          }}
                          <v-chip x-small color="success" class="ml-1">
                            Assigned
                          </v-chip>
                        </span>
                        <v-btn
                          v-else-if="order.order_status == 1 || order.order_status == 2 || order.order_status == 3"
                          x-small
                          color="warning"
                          @click="openAssignTransporterDialog(order)"
                        >
                          <v-icon small left>mdi-account-plus</v-icon>
                          Assign
                        </v-btn>
                        <span v-else class="grey--text text--darken-1">
                          Not Assigned
                        </span>
                      </td>
                      <td v-if="channel !== 'counter'">
                        {{ order.delivered_at | niceDate }}
                      </td>
                      <!-- Order Status Dropdown Column -->
                      <td v-if="channel !== 'counter'">
                        <!-- Show dropdown only for confirmed orders (status 1, 2, 3) -->
                        <v-select
                          v-if="order.order_status == 1 || order.order_status == 2 || order.order_status == 3"
                          :value="null"
                          :items="getNextStatusOptions(order.order_status)"
                          item-text="text"
                          item-value="value"
                          dense
                          outlined
                          hide-details
                          style="min-width: 160px"
                          placeholder="Select Status"
                          @change="updateOrderStatusFromDropdown(order, $event)"
                          :loading="order.updatingStatus"
                        ></v-select>
                        <!-- Show chip for other statuses (Pending, Delivered, Cancelled, Returned) -->
                        <v-chip
                          v-else-if="order.order_status == 0"
                          small
                          color="orange"
                          text-color="white"
                        >
                          Pending
                        </v-chip>
                        <v-chip
                          v-else-if="order.order_status == 4"
                          small
                          color="green darken-2"
                          text-color="white"
                        >
                          Delivered
                        </v-chip>
                        <v-chip
                          v-else-if="order.order_status == 5"
                          small
                          color="red"
                          text-color="white"
                        >
                          Cancelled
                        </v-chip>
                        <v-chip
                          v-else-if="order.order_status == 6"
                          small
                          color="red"
                          text-color="white"
                        >
                          Returned
                        </v-chip>
                      </td>
                      <td class="text-right">
                        <template v-if="isDeletePage() != true">
                          <v-tooltip
                            bottom
                            v-if="
                              order.order_status == 0 &&
                              order.created_by &&
                              order.created_by.position &&
                              order.created_by.position.name === 'CUSTOMER'
                            "
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                class="my-auto"
                                color="success"
                                x-large
                                @click="handleStatus(order, 1)"
                              >
                                <v-icon dark> mdi-check-circle-outline </v-icon>
                              </v-btn>
                            </template>
                            <span>Confirm this Order</span>
                          </v-tooltip>
                          <v-tooltip
                            bottom
                            v-if="
                              order.order_status == 0 &&
                              order.created_by &&
                              order.created_by.position &&
                              order.created_by.position.name === 'CUSTOMER'
                            "
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                class="mx-1"
                                color="red"
                                x-large
                                @click="handleStatus(order, 5)"
                              >
                                <v-icon dark> mdi-close-circle-outline </v-icon>
                              </v-btn>
                            </template>
                            <span>Reject this Order</span>
                          </v-tooltip>
                          <v-tooltip bottom v-if="can('ORDERS.SINGLE_VIEW')">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                class="mx-2 mb-2"
                                fab
                                x-small
                                color="primary"
                                :to="`/orders/${encryptIt(
                                  order.id
                                )}/order-details`"
                              >
                                <v-icon dark>mdi-content-duplicate</v-icon>
                              </v-btn>
                            </template>
                            <span>View Order Details</span>
                          </v-tooltip>

                          <!-- Refund Button: Only for Online Payment + Cancelled Orders + Not Yet Refunded -->
                          <v-tooltip
                            bottom
                            v-if="
                              order.payment_mode === 1 &&
                              order.order_status === 5 &&
                              !order.razorpay_refund_id &&
                              isAdmin
                            "
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                class="mx-2 mb-2"
                                fab
                                x-small
                                color="success"
                                @click="openRefundDialog(order)"
                                :loading="refundLoading === order.id"
                              >
                                <v-icon dark>mdi-cash-refund</v-icon>
                              </v-btn>
                            </template>
                            <span>Process Refund</span>
                          </v-tooltip>

                          <!-- Replacement: same product, same / other size - no time limit, and replacement orders can be replaced again -->
                          <v-tooltip
                            bottom
                            v-if="order.order_status == 4 && can('REPLACEMENTS.CREATE')"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                class="mx-2 mb-2"
                                fab
                                x-small
                                color="blue"
                                @click="$router.push(`/replacements?order_id=${order.id}`)"
                              >
                                <v-icon dark>mdi-swap-horizontal</v-icon>
                              </v-btn>
                            </template>
                            <span>Replace Items</span>
                          </v-tooltip>

                          <!-- Sale Return (refund): admin-only fallback when no replacement is possible -->
                          <v-tooltip
                            bottom
                            v-if="order.order_status == 4 && can('SALE_RETURNS.CREATE')"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                class="mx-2 mb-2"
                                fab
                                x-small
                                color="orange"
                                @click="$router.push(`/sale-returns?order_id=${order.id}`)"
                              >
                                <v-icon dark>mdi-keyboard-return</v-icon>
                              </v-btn>
                            </template>
                            <span>Return &amp; Refund (fallback)</span>
                          </v-tooltip>

                          <!-- Edit the items: stock moves by the difference only -->
                          <v-tooltip bottom v-if="can('ORDERS.UPDATE') && !order.parent_order_id && ![5, 6].includes(Number(order.order_status))">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                class="mx-2 mb-2"
                                fab
                                x-small
                                color="primary"
                                @click="openEditSale(order)"
                              >
                                <v-icon dark>mdi-pencil</v-icon>
                              </v-btn>
                            </template>
                            <span>Edit this Sale</span>
                          </v-tooltip>

                          <!-- Delete: undoes its returns / replacements, gives the stock back and removes its payments - asks first -->
                          <v-tooltip bottom v-if="can('ORDERS.DELETE')">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                class="mx-2 mb-2"
                                fab
                                x-small
                                color="error"
                                @click="confirmDeleteOrder(order)"
                              >
                                <v-icon dark>mdi-delete</v-icon>
                              </v-btn>
                            </template>
                            <span>Delete this Order</span>
                          </v-tooltip>

                          <!-- <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                class="ml-2"
                                color="primary"
                                :to="`/orders/${encryptIt(order.id)}`"
                              >
                                <v-icon dark> mdi-pencil </v-icon>
                              </v-btn>
                            </template>
                            <span>Edit this Order</span>
                          </v-tooltip> -->
                          <!-- <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                class="my-auto"
                                color="error"
                                @click="Delete_this(order.id)"
                              >
                                <v-icon dark> mdi-delete </v-icon>
                              </v-btn>
                            </template>
                            <span>Delete this Order</span>
                          </v-tooltip> -->
                        </template>
                        <template v-else>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                class="my-auto"
                                color="error"
                                @click="Restore_this(order.id)"
                              >
                                <v-icon dark> mdi-delete-restore </v-icon>
                              </v-btn>
                            </template>
                            <span>Restore this order</span>
                          </v-tooltip>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td :colspan="channel === 'counter' ? 15 : 18">
                        <AppEmptyState
                          icon="mdi-cart"
                          title="No orders yet"
                          message="Orders from customers or the counter will show up here."
                        />
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination rounded v-model="page" :length="paginationLength">
              </v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-dialog v-model="is_excel_dialog" width="600">
        <v-card>
          <v-card-title>Generate Orders with GST Excel</v-card-title>
          <v-divider class="mb-5"></v-divider>
          <v-card-text>
            <v-row>
              <v-col sm="12">
                <v-autocomplete
                  ref="user"
                  outlined
                  v-model="filter.gst_no"
                  :items="gstItems"
                  label="GST Number"
                  prepend-inner-icon="mdi-account"
                  clearable
                  :error-messages="errors.gst_no"
                ></v-autocomplete>
              </v-col>

              <!-- Start date -->
              <v-col sm="6">
                <v-menu
                  v-model="start_date_menu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      outlined
                      v-model="filter.start_month_year"
                      label="Year - Month"
                      append-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      clearable
                      @click:clear="filter.start_month_year = null"
                      :error-messages="errors.start_month_year"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="filter.start_month_year"
                    type="month"
                    @change="start_date_menu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <!-- /Start date -->
              <!-- End date -->
              <v-col sm="6">
                <v-menu
                  v-model="end_date_menu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      outlined
                      v-model="filter.end_month_year"
                      label="Year - Month"
                      append-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      clearable
                      @click:clear="filter.end_month_year = null"
                      :error-messages="errors.end_month_year"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="filter.end_month_year"
                    type="month"
                    @change="end_date_menu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <!-- /End date -->
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="gray" @click="is_excel_dialog = false">
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              @click="generateGstExcel"
              :loading="isGstExcelLoading"
            >
              Download
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- Clear Success Dialog Box -->
      <v-dialog v-model="is_deleted_order" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-empty
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Orders deleted successfully.</h3>
                      <br />
                      <v-btn color="success" @click="is_deleted_order = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
      <!-- Restore Success Dialog Box -->
      <v-dialog v-model="is_restored_store" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Order restored successfully.</h3>
                      <br />
                      <v-btn color="success" @click="is_restored_store = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Assign Transporter Manager Dialog -->
      <v-dialog v-model="assignTransporterDialog" max-width="500">
        <v-card rounded="xl">
          <v-card-title class="text-h5 primary white--text">
            <v-icon left color="white">mdi-truck-delivery</v-icon>
            Assign Transporter Manager
          </v-card-title>
          <v-card-text class="pt-4">
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="selectedTransporterId"
                  :items="transporterManagers"
                  item-text="text"
                  item-value="value"
                  label="Select Transporter Manager"
                  prepend-icon="mdi-account-tie"
                  outlined
                  dense
                  :loading="loadingTransporters"
                  :error-messages="errors.transporter_manager_id"
                >
                </v-autocomplete>
              </v-col>
              <v-col cols="12" v-if="selectedOrder">
                <v-alert type="info" text dense>
                  <strong>Order No:</strong> {{ selectedOrder.order_no }}<br />
                  <strong>Customer:</strong>
                  {{
                    selectedOrder.customer
                      ? selectedOrder.customer.first_name +
                        " " +
                        selectedOrder.customer.last_name
                      : "N/A"
                  }}
                </v-alert>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="closeAssignTransporterDialog">
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              @click="assignTransporterManager"
              :loading="assigningTransporter"
              :disabled="!selectedTransporterId"
            >
              Assign
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Manual Refund Dialog -->
      <v-dialog v-model="refundDialog" max-width="500">
        <v-card rounded="xl">
          <v-card-title class="text-h5 success white--text">
            <v-icon left color="white">mdi-cash-refund</v-icon>
            Process Manual Refund
          </v-card-title>
          <v-card-text class="pt-4">
            <v-row>
              <!-- Error Message Alert -->
              <v-col cols="12" v-if="refundError">
                <v-alert type="error" dense dismissible @input="refundError = null">
                  <strong>Error:</strong> {{ refundError }}
                </v-alert>
              </v-col>

              <v-col cols="12" v-if="selectedOrderForRefund">
                <v-alert type="warning" text dense>
                  <strong>⚠️ Confirm Refund Details</strong>
                </v-alert>
                <v-alert type="info" text dense class="mt-3">
                  <strong>Order No:</strong>
                  {{ selectedOrderForRefund.order_no }}<br />
                  <strong>Customer:</strong>
                  {{
                    selectedOrderForRefund.customer
                      ? selectedOrderForRefund.customer.first_name +
                        " " +
                        selectedOrderForRefund.customer.last_name
                      : "N/A"
                  }}<br />
                  <strong>Refund Amount:</strong> ₹{{
                    selectedOrderForRefund.total_amount
                  }}
                </v-alert>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="refundRemarks"
                  label="Refund Remarks (Optional)"
                  prepend-icon="mdi-note-text"
                  outlined
                  dense
                  rows="3"
                  placeholder="Enter reason or additional notes for this refund..."
                ></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-alert type="info" dense text>
                  This will credit ₹{{
                    selectedOrderForRefund
                      ? selectedOrderForRefund.total_amount
                      : 0
                  }}
                  to the customer's wallet and mark the order as refunded via
                  Razorpay.
                </v-alert>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="refundDialog = false">
              Cancel
            </v-btn>
            <v-btn
              color="success"
              @click="processRefund"
              :loading="refundLoading !== null"
              :disabled="!selectedOrderForRefund"
            >
              <v-icon left>mdi-check</v-icon>
              Confirm Refund
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete a sale: lists what goes with it first -->
      <v-dialog v-model="showDeleteOrder" max-width="480">
        <v-card v-if="orderToDelete">
          <v-card-title class="text-subtitle-1 font-weight-bold">Delete {{ orderToDelete.order_no }}?</v-card-title>
          <v-card-text v-if="!deleteOrderPreview" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
          </v-card-text>
          <v-card-text v-else-if="deleteOrderPreview.blocked">
            <v-alert type="warning" dense text class="mb-0">{{ deleteOrderPreview.blocked }}</v-alert>
          </v-card-text>
          <v-card-text v-else>
            <p class="mb-2">This will also:</p>
            <ul class="mb-3 delete-list">
              <li v-if="(deleteOrderPreview.replacements || []).length">
                Delete {{ deleteOrderPreview.replacements.length }} replacement{{ deleteOrderPreview.replacements.length === 1 ? "" : "s" }}
                (<strong>{{ deleteOrderPreview.replacements.map((r) => r.number).join(", ") }}</strong>)
                <template v-if="(deleteOrderPreview.replacement_orders || []).length">
                  and {{ deleteOrderPreview.replacement_orders.length === 1 ? "its replacement order" : "their replacement orders" }}
                </template>
                - the new sizes go back into stock and the pieces taken back come out again
              </li>
              <li v-if="(deleteOrderPreview.returns || []).length">
                Delete {{ deleteOrderPreview.returns.length }} return{{ deleteOrderPreview.returns.length === 1 ? "" : "s" }}
                (<strong>{{ deleteOrderPreview.returns.map((r) => r.number).join(", ") }}</strong>)
                - returned pieces come out of stock / Return Stock again
                <template v-if="deleteOrderPreview.returns.some((r) => r.wallet_refund > 0)">
                  and ₹{{ formatMoney(deleteOrderPreview.returns.reduce((s, r) => s + r.wallet_refund, 0)) }} wallet refund is taken back
                </template>
              </li>
              <li v-if="deleteOrderPreview.restores_stock">
                Put its items back into <strong>{{ deleteOrderPreview.stock_to }}</strong>:
                {{ deleteOrderPreview.items.join(", ") }}
              </li>
              <li v-else>Leave stock as it is - this sale was cancelled / returned, so its stock is already back</li>
              <li v-if="deleteOrderPreview.payments_count">
                Delete {{ deleteOrderPreview.payments_count }} payment record{{ deleteOrderPreview.payments_count === 1 ? "" : "s" }}
                (₹{{ formatMoney(deleteOrderPreview.payments_amount) }})
              </li>
            </ul>
            <p class="mb-0 grey--text text--darken-1">
              Stock ends up exactly where it was before this sale.
              <template v-if="(deleteOrderPreview.returns || []).length || (deleteOrderPreview.replacements || []).length">
                Because its returns / replacements go with it, this can't be undone.
              </template>
              <template v-else>You can restore it from the deleted orders; the stock is taken again and the payments come back.</template>
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showDeleteOrder = false">Cancel</v-btn>
            <v-btn
              color="error"
              depressed
              :loading="isDeletingOrder"
              :disabled="!deleteOrderPreview || !!deleteOrderPreview.blocked"
              @click="deleteOrder"
            >
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Edit a sale's items -->
      <EditSaleDialog :show.sync="showEditSale" :order-id="orderToEditId" @saved="getData" />

      <!-- Confirmation Dialog Component -->
      <ConfirmationDialog
        :show.sync="dialog"
        :title="dialogTitle"
        :message="dialogMessage"
        :icon="dialogIcon"
        :color="dialogColor"
        :isRejection="isRejection"
        :loading="isConfirming"
        @confirm="confirmApproval"
      />
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import ConfirmationDialog from "@/components/ConfirmationDialog.vue";
import AppEmptyState from "@/components/AppEmptyState.vue";
import AppPageHeader from "@/components/AppPageHeader.vue";
import EditSaleDialog from "./EditSaleDialog.vue";

export default {
  components: { ConfirmationDialog, AppEmptyState, AppPageHeader, EditSaleDialog },
  name: "OrdersPage",
  data() {
    return {
      filter: {
        product_id: "",
        position_id: "",
        user_id: "",
        store_id: "",
        vehicle_maker: "",
        state: "",
        search_keyword: "",
        is_active: "",
        order_status: "",
        payment_status: "",
        is_selected: false,
        month_year: "",
        start_date: "",
        end_date: "",
        start_month_year: "",
        end_month_year: "",
        gst_no: "",
      },
      channelTab: 0,
      stats: {
        total_sales: 0, total_orders: 0, avg_order_value: 0,
        delivered_amount: 0, delivered_count: 0,
        pending_amount: 0, pending_count: 0,
        cancelled_count: 0, returned_count: 0,
        channels: { app: { sales: 0, orders: 0 }, counter: { sales: 0, orders: 0 } },
        pipeline: [],
        needs_action: { stale_count: 0, stale_amount: 0, unassigned_count: 0 },
        payment: { cash_amount: 0, online_amount: 0, unpaid_amount: 0, unpaid_count: 0 },
      },
      products: 0,
      productItems: [],
      vehicle_makersItems: [],
      usersItems: [],
      positionItems: [],
      storeItems: [],
      orders: [],
      stores: [],
      gstItems: [],
      statusItems: [
        {
          id: 0,
          text: "Select Active Status",
          value: "",
        },
        { id: 1, text: "Pending", value: "0" },
        { id: 2, text: "Confirmed", value: "1" },
        { id: 3, text: "Parcel Packaged", value: "2" },
        { id: 4, text: "Out for Delivery", value: "3" },
        { id: 5, text: "Delivered", value: "4" },
        { id: 6, text: "Cancelled", value: "5" },
        { id: 7, text: "Returned", value: "6" },
      ],
      paymentStatusItems: [
        {
          id: 0,
          text: "Select Payment Status",
          value: "",
        },
        { id: 1, text: "Pending", value: "0" },
        { id: 2, text: "Paid", value: "1" },
        { id: 3, text: "Failed", value: "2" },
        { id: 4, text: "Refunded", value: "3" },
        { id: 5, text: "Partial", value: "4" },
      ],
      statuses: [
        {
          id: 0,
          text: "Select Active Status",
          value: "",
        },
        { id: "ACTIVE", text: "ACTIVE", value: "1" },
        { id: "INACTIVE", text: "INACTIVE", value: "0" },
      ],
      ExportOrderTypes: [],
      page: 1,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      excel_fields: {
        "Sr No": "sr_no",
        Name: "name",
      },
      isLoading: false,
      is_restored_store: false,
      isExcelloading: false,
      isGstExcelLoading: false,
      delete_order: false,
      showEditSale: false,
      orderToEditId: null,
      showDeleteOrder: false,
      orderToDelete: null,
      deleteOrderPreview: null,
      isDeletingOrder: false,
      is_deleted_order: false,
      is_select_all: false,
      isDeleteAll: false,
      month_menu: false,
      start_menu: false,
      end_menu: false,
      start_date_menu: false,
      end_date_menu: false,
      is_excel_dialog: false,

      selectedOrderId: null,
      selectedOrderStatus: null,
      isRejection: false,
      isConfirming: false,
      dialog: false,
      dialogTitle: "",
      dialogMessage: "",
      dialogIcon: "",
      dialogColor: "",

      // Order Status Dropdown Items (for confirmed orders)
      orderStatusDropdownItems: [
        { text: "Confirmed", value: 1 },
        { text: "Parcel Packaged", value: 2 },
        { text: "Out for Delivery", value: 3 },
        { text: "Delivered", value: 4 },
      ],

      // Transporter Manager Assignment
      assignTransporterDialog: false,
      selectedOrder: null,
      selectedTransporterId: null,
      transporterManagers: [],
      loadingTransporters: false,
      assigningTransporter: false,

      // Manual Refund
      refundDialog: false,
      selectedOrderForRefund: null,
      refundLoading: null,
      refundRemarks: "",
      refundError: null, // Store error message to display in dialog
    };
  },
  watch: {
    page: "getData",
    channelTab() {
      // Stage filters belong to the app pipeline; drop it when leaving.
      if (this.channel === "counter") this.filter.order_status = "";
      this.page = 1;
      this.getData();
    },
    $route() {
      this.getData();
      this.getMasters();
    },
  },
  mounted() {
    this.getData();
    this.fetchTransporterManagers();
    // this.getMasters();
  },
  methods: {
    getPositionAbbr(positionName) {
      if (!positionName) return "";

      const abbreviations = {
        CUSTOMER: "Cus",
        "DISTRIBUTOR/DEALER": "Dis",
        "FRANCHISE/OUTLET PARTNER": "Fra",
        "SALE EXECUTIVE": "SE",
      };

      return (
        abbreviations[positionName.toUpperCase()] ||
        positionName.substring(0, 3)
      );
    },
    // async getMasters() {
    //   await axios
    //     .get(`orders/masters`)
    //     .then((masters) => {
    //       masters = masters.data;
    //       //Product
    //       masters.products.forEach((product) => {
    //         this.productItems.push({
    //           id: product.id,
    //           text: product.name,
    //           value: product.id,
    //         });
    //       });
    //       //State
    //       masters.states.forEach((state) => {
    //         this.stateItems.push({
    //           id: state.id,
    //           text: state.name,
    //           value: state.name,
    //         });
    //       });
    //       //Store
    //       masters.stores.forEach((store) => {
    //         this.storeItems.push({
    //           id: store.id,
    //           text: store.name,
    //           value: store.id,
    //         });
    //         this.gstItems.push({
    //           id: store.id,
    //           text: store.gst_no,
    //           value: store.gst_no,
    //         });
    //       });
    //       //Position
    //       masters.positions.forEach((position) => {
    //         this.positionItems.push({
    //           id: position.id,
    //           text: position.name,
    //           value: position.id,
    //         });
    //       });
    //       //User
    //       masters.users.forEach((user) => {
    //         this.usersItems.push({
    //           id: user.id,
    //           text: user.first_name,
    //           value: user.id,
    //         });
    //       });
    //       //User
    //       masters.vehicle_makers.forEach((vehicle_maker) => {
    //         this.vehicle_makersItems.push({
    //           id: vehicle_maker.id,
    //           text: vehicle_maker.name,
    //           value: vehicle_maker.name,
    //         });
    //       });
    //     })
    //     .catch(function (error) {
    //       console.log(error);
    //       // window.location.href = "/auth/login";
    //     });
    // },
    // Blank out unset filters so they serialize as empty params rather than
    // "undefined". Shared by the list and the summary cards.
    normalizeFilters() {
      this.filter.search_keyword = this.filter.search_keyword
        ? this.filter.search_keyword
        : "";
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.filter.is_active = this.filter.is_active
        ? this.filter.is_active
        : "";
      this.filter.product_id = this.filter.product_id
        ? this.filter.product_id
        : "";
      this.filter.user_id = this.filter.user_id ? this.filter.user_id : "";
      this.filter.order_status = this.filter.order_status
        ? this.filter.order_status
        : "";
      this.filter.payment_status = this.filter.payment_status
        ? this.filter.payment_status
        : "";
      this.filter.state = this.filter.state ? this.filter.state : "";
      this.filter.vehicle_maker = this.filter.vehicle_maker
        ? this.filter.vehicle_maker
        : "";
      this.filter.city = this.filter.city ? this.filter.city : "";
      this.filter.position_id = this.filter.position_id
        ? this.filter.position_id
        : "";
      this.filter.month_year = this.filter.month_year
        ? this.filter.month_year
        : "";
      this.filter.start_date = this.filter.start_date
        ? this.filter.start_date
        : "";
      this.filter.end_date = this.filter.end_date ? this.filter.end_date : "";
      if (this.$route.query.store_id != null) {
        this.filter.store_id = this.decryptIt(this.$route.query.store_id);
      } else {
        this.filter.store_id = "";
      }
    },

    // Everything except page/rowsPerPage, so /orders and /orders/stats always
    // describe the same set of rows.
    filterQueryString() {
      return (
        "role_id=2" +
        "&show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&product_id=" +
        this.filter.product_id +
        "&user_id=" +
        this.filter.user_id +
        "&order_status=" +
        this.filter.order_status +
        "&payment_status=" +
        this.filter.payment_status +
        "&store_id=" +
        this.filter.store_id +
        "&position_id=" +
        this.filter.position_id +
        "&state=" +
        this.filter.state +
        "&city=" +
        this.filter.city +
        "&month_year=" +
        this.filter.month_year +
        "&start_date=" +
        this.filter.start_date +
        "&end_date=" +
        this.filter.end_date +
        "&search_keyword=" +
        this.filter.search_keyword +
        "&channel=" +
        this.channel
      );
    },

    // Clicking a pipeline card filters the list to that stage; clicking the
    // active one clears it again.
    filterByStage(status) {
      this.filter.order_status =
        String(this.filter.order_status) === String(status) ? "" : status;
      this.page = 1;
      this.getData();
    },

    async getData() {
      this.isLoading = true;
      this.normalizeFilters();
      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&" +
        this.filterQueryString();
      await axios
        .get(`orders?${query}`)
        .then((response) => {
          this.orders = response.data.data;
          this.products = response.data.data1;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
          // window.location.href = "/auth/login";
        });
      this.isLoading = false;
      this.getStats();
    },

    async getStats() {
      this.normalizeFilters();
      try {
        const response = await axios.get(
          `orders/stats?${this.filterQueryString()}`,
        );
        this.stats = response.data.data || {};
      } catch (error) {
        console.log(error);
      }
    },

    formatAmount(value) {
      const amount = Number(value || 0);
      return `₹ ${amount.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    formatDate(dateStr) {
      if (!dateStr || dateStr === "") {
        return "";
      }
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        return "";
      }
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    async Delete_this(id) {
      this.delete_order = true;
      await axios
        .post(`/orders/delete/${this.encryptIt(id)}`)
        .then(() => {
          this.delete_order = false;
          this.is_deleted_order = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
    },
    selectAll() {
      console.log(this.is_select_all);
      this.orders.forEach((order) => {
        order.is_selected = this.is_select_all;
      });
    },
    async deleteAll() {
      this.isDeleteAll = true;
      let deleteallIds = [];
      this.orders.forEach((order) => {
        if (order.is_selected) deleteallIds.push(order);
      });
      await axios
        .post("/orders/alldelete", { deleteallIds })
        .then(() => {
          this.filter.status = "";
          this.getData();
        })
        .catch(function (error) {
          this.getData();
          console.log(error);
        });
      // let response = await axios.post(/sync_users/upstart_datert_date, this.form);
      this.isDeleteAll = false;
    },
    //Generate Excel
    async generateExcel() {
      this.isExcelLoading = true;
      this.filter.search_keyword = this.filter.search_keyword
        ? this.filter.search_keyword
        : "";
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.filter.is_active = this.filter.is_active
        ? this.filter.is_active
        : "";
      this.filter.product_id = this.filter.product_id
        ? this.filter.product_id
        : "";
      this.filter.user_id = this.filter.user_id ? this.filter.user_id : "";
      this.filter.state = this.filter.state ? this.filter.state : "";
      this.filter.vehicle_maker = this.filter.vehicle_maker
        ? this.filter.vehicle_maker
        : "";
      this.filter.city = this.filter.city ? this.filter.city : "";
      this.filter.position_id = this.filter.position_id
        ? this.filter.position_id
        : "";
      this.filter.month_year = this.filter.month_year
        ? this.filter.month_year
        : "";
      this.filter.start_date = this.filter.start_date
        ? this.filter.start_date
        : "";
      this.filter.end_date = this.filter.end_date ? this.filter.end_date : "";
      if (this.$route.query.store_id != null) {
        this.filter.store_id = this.decryptIt(this.$route.query.store_id);
      } else {
        this.filter.store_id = "";
      }
      let query =
        "&role_id=2" +
        "&companyId=" +
        this.company.id +
        "&is_active=" +
        this.filter.is_active +
        "&product_id=" +
        this.filter.product_id +
        "&user_id=" +
        this.filter.user_id +
        "&store_id=" +
        this.filter.store_id +
        "&position_id=" +
        this.filter.position_id +
        "&state=" +
        this.filter.state +
        "&vehicle_maker=" +
        this.filter.vehicle_maker +
        "&city=" +
        this.filter.city +
        "&start_date=" +
        this.filter.start_date +
        "&end_date=" +
        this.filter.end_date +
        "&month_year=" +
        this.filter.month_year +
        "&search_keyword=" +
        this.filter.search_keyword;
      const excelResponse = await axios.get(`/orders/excel_export?${query}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([excelResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orders_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      this.isExcelloading = false;
    },
    async generateGstExcel() {
      this.isGstExcelLoading = true;
      this.filter.is_active = this.filter.is_active
        ? this.filter.is_active
        : "";
      this.filter.start_month_year = this.filter.start_month_year
        ? this.filter.start_month_year
        : "";
      this.filter.end_month_year = this.filter.end_month_year
        ? this.filter.end_month_year
        : "";
      let query =
        "&role_id=2" +
        "&companyId=" +
        this.company.id +
        "&is_active=" +
        this.filter.is_active +
        "&gst_no=" +
        this.filter.gst_no +
        "&start_month_year=" +
        this.filter.start_month_year +
        "&end_month_year=" +
        this.filter.end_month_year;
      const gstResponse = await axios.get(`/orders/gst_excel_export?${query}`, { responseType: 'blob' });
      this.filter.start_month_year = "";
      this.filter.end_month_year = "";
      this.filter.gst_no = "";
      const gstUrl = window.URL.createObjectURL(new Blob([gstResponse.data]));
      const gstLink = document.createElement('a');
      gstLink.href = gstUrl;
      gstLink.setAttribute('download', `orders_gst_${Date.now()}.xlsx`);
      document.body.appendChild(gstLink);
      gstLink.click();
      gstLink.remove();
      window.URL.revokeObjectURL(gstUrl);
      this.isGstExcelLoading = false;
      this.is_excel_dialog = false;
    },
    async Restore_this(id) {
      this.restore_order = true;
      await axios
        .post(`/orders/restore/${this.encryptIt(id)}`)
        .then(() => {
          this.restore_order = false;
          this.is_restored_store = true;
          this.getData();
        })
        .catch((error) => {
          console.log(error);
          this.restore_order = false;
          // e.g. not enough stock left to take the sale's items again
          const message = error.response && error.response.data && error.response.data.message;
          if (this.$toast) this.$toast.error(message || "Could not restore the order.");
        });
    },
    openEditSale(order) {
      this.orderToEditId = order.id;
      this.showEditSale = true;
    },
    async confirmDeleteOrder(order) {
      this.orderToDelete = order;
      this.deleteOrderPreview = null;
      this.showDeleteOrder = true;
      try {
        const response = await axios.get(`/orders/delete_preview/${this.encryptIt(order.id)}`);
        if (this.orderToDelete === order) this.deleteOrderPreview = response.data.data;
      } catch (error) {
        console.log(error);
        this.showDeleteOrder = false;
        if (this.$toast) this.$toast.error("Could not load the order.");
      }
    },
    async deleteOrder() {
      this.isDeletingOrder = true;
      try {
        await axios.post(`/orders/delete/${this.encryptIt(this.orderToDelete.id)}`);
        this.showDeleteOrder = false;
        if (this.$toast) this.$toast.success(`${this.orderToDelete.order_no} deleted.`);
        this.getData();
      } catch (error) {
        console.log(error);
        const message = error.response && error.response.data && error.response.data.message;
        if (this.$toast) this.$toast.error(message || "Could not delete the order.");
      }
      this.isDeletingOrder = false;
    },
    formatMoney(value) {
      return (Number(value) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    handleStatus(order, status) {
      const isApproval = status === 1;
      this.dialogTitle = isApproval ? "Confirm Order" : "Reject Order";

      this.dialogMessage = isApproval
        ? "Are you sure you want to confirm this Order?"
        : "Are you sure you want to reject this Order?";

      this.dialogIcon = isApproval
        ? "mdi-check-circle-outline"
        : "mdi-alert-circle-outline";

      this.dialogColor = isApproval ? "green" : "red";

      this.selectedOrderId = order.id;
      this.selectedOrderStatus = status;
      this.isRejection = !isApproval;
      this.dialog = true; // Open dialog
    },
    async confirmApproval(rejectionReason) {
      const payload = {
        order_status: this.selectedOrderStatus,
        remarks: this.selectedOrderStatus === 5 ? rejectionReason : null,
      };
      console.log("order", this.encryptIt(this.selectedOrderId));

      this.isConfirming = true;

      try {
        await axios.patch(
          `/orders/${this.encryptIt(this.selectedOrderId)}`,
          payload
        );

        // Close dialog first
        this.dialog = false;

        // Refresh data
        this.getData();

        // Show toast notification
        if (this.$toast) {
          if (this.selectedOrderStatus == 1) {
            this.$toast.success("Order confirmed successfully!", {
              position: "top-right",
              timeout: 3000,
            });
          } else {
            this.$toast.error("Order rejected!", {
              position: "top-right",
              timeout: 3000,
            });
          }
        }
      } catch (error) {
        console.log(error);
        // Close dialog on error too
        this.dialog = false;
        if (this.$toast) {
          this.$toast.error(
            error.response?.data?.message || "Failed to update order status",
            {
              position: "top-right",
              timeout: 3000,
            }
          );
        }
      } finally {
        this.isConfirming = false;
        this.selectedOrderId = "";
        this.selectedOrderStatus = "";
      }
    },

    // Transporter Manager Methods
    async fetchTransporterManagers() {
      this.loadingTransporters = true;
      try {
        const response = await axios.get(`/users`, {
          params: {
            position_name: "TRANSPORTER MANAGER",
            is_active: 1,
            rowsPerPage: 1000,
          },
        });

        this.transporterManagers = response.data.data.map((user) => ({
          text: `${user.first_name} ${user.last_name} (${user.email})`,
          value: user.id,
        }));
      } catch (error) {
        console.error("Error fetching transporter managers:", error);
        this.$toast.error("Failed to load transporter managers");
      } finally {
        this.loadingTransporters = false;
      }
    },

    openAssignTransporterDialog(order) {
      this.selectedOrder = order;
      this.selectedTransporterId = order.transporter_manager_id || null;
      this.assignTransporterDialog = true;
    },

    closeAssignTransporterDialog() {
      this.assignTransporterDialog = false;
      this.selectedOrder = null;
      this.selectedTransporterId = null;
    },

    async assignTransporterManager() {
      if (!this.selectedTransporterId || !this.selectedOrder) {
        return;
      }

      this.assigningTransporter = true;
      try {
        await axios.post(`/orders/assign-transporter`, {
          order_id: this.selectedOrder.id,
          transporter_manager_id: this.selectedTransporterId,
        });

        if (this.$toast) {
          this.$toast.success("Transporter manager assigned successfully");
        }
        this.closeAssignTransporterDialog();
        this.getData(); // Refresh orders list
      } catch (error) {
        console.error("Error assigning transporter:", error);
        if (this.$toast) {
          this.$toast.error(
            error.response?.data?.message ||
              "Failed to assign transporter manager"
          );
        }
      } finally {
        this.assigningTransporter = false;
      }
    },

    // Get next available status options based on current status
    getNextStatusOptions(currentStatus) {
      const allOptions = [
        { text: "Parcel Packaged", value: 2 },
        { text: "Out for Delivery", value: 3 },
        { text: "Delivered", value: 4 },
      ];

      // Filter to show only statuses greater than current status
      return allOptions.filter(option => option.value > currentStatus);
    },

    async updateOrderStatusFromDropdown(order, newStatus) {
      if (!newStatus) return;

      const previousStatus = order.order_status;

      try {
        this.$set(order, "updatingStatus", true);
        await axios.post(
          `/orders/${this.encryptIt(order.id)}/update-status`,
          { order_status: newStatus }
        );

        // Update order status locally
        order.order_status = newStatus;

        if (this.$toast) {
          this.$toast.success("Order status updated successfully");
        }

        // Refresh to update the UI
        this.getData();
      } catch (error) {
        console.error("Error updating order status:", error);
        // Revert to previous status on error
        order.order_status = previousStatus;
        if (this.$toast) {
          this.$toast.error(
            error.response?.data?.message || "Failed to update order status"
          );
        }
      } finally {
        this.$set(order, "updatingStatus", false);
      }
    },

    async updateOrderStatus(order) {
      const previousStatus = order._previousStatus || order.order_status;
      order._previousStatus = previousStatus;

      try {
        this.$set(order, "updatingStatus", true);
        await axios.post(
          `/orders/${this.encryptIt(order.id)}/update-status`,
          { order_status: order.order_status }
        );

        if (this.$toast) {
          this.$toast.success("Order status updated successfully");
        }

        // If status is delivered (4), refresh to show chip instead of dropdown
        if (order.order_status == 4) {
          this.getData();
        }
      } catch (error) {
        console.error("Error updating order status:", error);
        // Revert to previous status on error
        order.order_status = previousStatus;
        if (this.$toast) {
          this.$toast.error(
            error.response?.data?.message || "Failed to update order status"
          );
        }
      } finally {
        this.$set(order, "updatingStatus", false);
      }
    },

    // Open Refund Confirmation Dialog
    openRefundDialog(order) {
      this.selectedOrderForRefund = order;
      this.refundRemarks = "";
      this.refundError = null; // Clear any previous error
      this.refundDialog = true;
    },

    // Process Manual Refund
    async processRefund() {
      if (!this.selectedOrderForRefund) return;

      // Clear any previous error
      this.refundError = null;
      this.refundLoading = this.selectedOrderForRefund.id;

      try {
        const response = await axios.post("/orders/manual-refund", {
          order_id: this.selectedOrderForRefund.id,
          remarks: this.refundRemarks || undefined,
        });

        if (response.data.success) {
          this.$toast.success(
            response.data.message || "Refund processed successfully!"
          );

          // Update order in the list to hide refund button
          const orderIndex = this.orders.findIndex(
            (o) => o.id === this.selectedOrderForRefund.id
          );
          if (orderIndex !== -1) {
            this.$set(
              this.orders[orderIndex],
              "razorpay_refund_id",
              response.data.data.order.razorpay_refund_id
            );
            this.$set(this.orders[orderIndex], "payment_status", 3); // Refunded
          }

          // Close dialog and reset only on success
          this.refundDialog = false;
          this.selectedOrderForRefund = null;
          this.refundRemarks = "";
          this.refundError = null;

          // Refresh orders list
          this.getData();
        }
      } catch (error) {
        console.error("Refund error:", error);

        // Extract error message
        const errorMessage =
          error.response?.data?.message || "Failed to process refund";

        // Show error in dialog
        this.refundError = errorMessage;

        // Also show toast for visibility
        this.$toast.error(errorMessage);

        // Keep dialog open to show the error
        // Don't reset selectedOrderForRefund or close dialog
      } finally {
        this.refundLoading = null;
      }
    },
  },

  computed: {
    // Tab index -> the channel value the API understands.
    channel() {
      return ["", "app", "counter"][this.channelTab] || "";
    },

    needsAction() {
      const n = this.stats.needs_action || {};
      return (n.stale_count || 0) + (n.unassigned_count || 0);
    },

    // Check if current user is admin
    isAdmin() {
      const user = this.$store.state.auth.user;
      if (!user || !user.roles) return false;

      // Check if user has any admin role
      return user.roles.some((role) =>
        role.name.toLowerCase().includes("admin")
      );
    },
  },
};
</script>

<style scoped>
.delete-list li {
  margin-bottom: 4px;
}
/* Lock the three rows to fixed heights so the labels, figures and sub-lines
   line up across all four cards even when one amount wraps. */
.stat-card .stat-label {
  min-height: 18px;
  opacity: 0.85;
}
.stat-card .stat-value {
  min-height: 34px;
  line-height: 34px;
  white-space: nowrap;
}
.stat-card .stat-sub {
  min-height: 18px;
  opacity: 0.85;
}
/* Pipeline cards double as stage filters. */
.pipeline-card {
  cursor: pointer;
  transition: border-color 0.15s;
  border: 2px solid transparent;
}
.pipeline-card:hover {
  border-color: #bdbdbd;
}
.pipeline-card.pipeline-active {
  border-color: #1976d2;
}
</style>
